import { useEffect, useMemo } from 'react';
import {
  PermissionEnum,
  useFilters,
  useGetList,
  usePrevious,
  useQueryState,
} from '@ui/src';
import { useLazyGetCustomersExternalOrganizationsQuery } from '../../../customers/store/services/customers';
import formatLinkedClientsList from '../helpers/format-linked-clients';
import { LinkedClient } from '../types';
import {
  linkedClientsFiltersSchema,
  linkedClientsSchema,
} from '../variables/linked-clients';
import { clearLinkedClientsState } from '../../store/slices/external-integration';
import { useAppDispatch } from '../../../../../../../store/use-store';
import {
  ExternalPspPartner,
  PspIntegrationPayment,
  PspIntegration,
} from '../../types';

const useLinkedClients = ({
  customerId,
  linkedClients,
  pspPartners,
  pspIntegrations,
  search,
  activePermissions,
}: {
  customerId?: string;
  linkedClients?: LinkedClient[];
  pspPartners?: ExternalPspPartner[];
  pspIntegrations?: PspIntegration[];
  search?: string;
  activePermissions?: PermissionEnum[];
}) => {
  const [
    getCustomersExternalOrganizations,
    { isLoading: linkedClientsIsLoading, isFetching: linkedClientsIsFetching },
  ] = useLazyGetCustomersExternalOrganizationsQuery();

  const { filters, onSortChange, onFilterChange } =
    useQueryState(linkedClientsSchema);

  const prevFilters = usePrevious(filters);

  const dispatch = useAppDispatch();

  const { getListAction, onFilter, setPage } = useGetList<LinkedClient>({
    getList: getCustomersExternalOrganizations,
    onSortChange,
    onFilterChange,
    schema: linkedClientsSchema,
    itemId: customerId,
  });

  const { filtersComponent, setValues, handleSubmit } = useFilters({
    filtersSchema: linkedClientsFiltersSchema,
    filters,
    onFilterChange,
  });

  const linkedClientsFormattedData = useMemo(() => {
    const data = formatLinkedClientsList(
      linkedClients,
      pspPartners?.map((item) => ({
        name: item.companyName,
        value: item.id,
      })),
    );

    return data;
  }, [pspPartners, linkedClients]);

  useEffect(() => {
    if (pspPartners && pspIntegrations) {
      const nextPspPartners = pspPartners.map((item) => {
        const result = { ...item };

        if (
          result.pspIntegrationId &&
          pspIntegrations?.find(
            (integration) => integration.id === result.pspIntegrationId,
          )
        ) {
          const currentItegration = pspIntegrations?.find(
            (integration) => integration.id === result.pspIntegrationId,
          );

          if (currentItegration) {
            result.pspIntegration = {
              name: currentItegration.name,
              pspIntegrationPayments:
                currentItegration.pspIntegrationPayments as PspIntegrationPayment[],
              id: currentItegration.id,
              externalCustomersSupported:
                currentItegration.externalCustomersSupported,
            };
          }
        }

        return result;
      });

      const pspPartnersWithExternalCustomerSupport = nextPspPartners?.filter(
        (item) => item?.pspIntegration?.externalCustomersSupported,
      );

      if (pspPartnersWithExternalCustomerSupport?.length) {
        setValues(
          'pspPartnerId',
          pspPartnersWithExternalCustomerSupport?.map((item) => ({
            name: item.companyName,
            value: item.id,
          })),
        );
      }
    }
  }, [pspPartners, pspIntegrations]);

  useEffect(() => {
    if (
      !linkedClients &&
      !search &&
      !Object.keys(filters).length &&
      customerId &&
      activePermissions?.includes(
        PermissionEnum.CUSTOMERS_EXTERNAL_ORGANIZATIONS_LIST,
      )
    ) {
      getListAction({});
    }
  }, [filters, search, linkedClients, customerId, activePermissions]);

  useEffect(() => {
    if (
      customerId &&
      !linkedClients &&
      search &&
      filters &&
      prevFilters &&
      Object.keys(prevFilters).length &&
      activePermissions?.includes(
        PermissionEnum.CUSTOMERS_EXTERNAL_ORGANIZATIONS_LIST,
      )
    ) {
      const filtersValues: any = {
        ...filters,
      };

      getListAction({ filters: filtersValues });
    }
  }, [
    filters,
    search,
    linkedClients,
    prevFilters,
    customerId,
    activePermissions,
  ]);

  useEffect(
    () => () => {
      dispatch(clearLinkedClientsState());
    },
    [],
  );

  return {
    linkedClientsFormattedData,
    filters,
    onFilter,
    filtersComponent,
    handleSubmit,
    linkedClientsIsLoading,
    linkedClientsIsFetching,
    setPage,
  };
};

export default useLinkedClients;
